import styled, {keyframes} from "styled-components";
import {useAtom} from "jotai";
import {modalController} from "../../store";
import {useEffect} from "react";

function Modal(props) {
  const [modalState] = useAtom(modalController);

  const modalWidth =
      modalState?.width ?? 500;

  const ModalContent =
      modalState?.modalComponent;

  if (
      !props.isShow ||
      typeof ModalContent !== "function"
  ) {
    return null;
  }

  return (
      <Dim id="modal">
        <ModalContainer
            $width={modalWidth}
            role="dialog"
            aria-modal="true"
        >
          <ModalContent />
        </ModalContainer>
      </Dim>
  );
}

export function ModalHeader(props) {
  const [, setModal] =
      useAtom(modalController);

  const modalClose = () => {
    if (props.onClose !== undefined) {
      props.onClose();
    }

    setModal({
      isShow: false,
      modalComponent: null
    });
  };

  useEffect(() => {
    const escKeyModalClose = event => {
      if (event.key === "Escape") {
        modalClose();
      }
    };

    window.addEventListener(
        "keydown",
        escKeyModalClose
    );

    return () =>
        window.removeEventListener(
            "keydown",
            escKeyModalClose
        );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
      <ModalHeaderContainer
          style={props.style}
      >
        <HeaderContent>
          <HeaderText>
            <HeaderAccent />

            <span>{props.title}</span>
          </HeaderText>

          {!props.closeButton && (
              <CloseButton
                  type="button"
                  aria-label="팝업 닫기"
                  title="닫기"
                  onClick={modalClose}
              >
                <span />
              </CloseButton>
          )}
        </HeaderContent>

        {props.description && (
            <HeaderDescription>
              {props.description}
            </HeaderDescription>
        )}
      </ModalHeaderContainer>
  );
}

export function ModalBody(props) {
  return (
      <ModalBodyContainer
          className={props.className}
          style={props.style}
      >
        {props.children}
      </ModalBodyContainer>
  );
}

export function ModalFooter(props) {
  return (
      <ModalFooterContainer
          className={props.className}
          style={props.style}
      >
        {props.children}
      </ModalFooterContainer>
  );
}

export default Modal;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const modalEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.985);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const Dim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
  background-color: rgba(28, 31, 35, 0.52);
  backdrop-filter: blur(2px);
  animation: ${fadeIn} 0.18s ease-out;

  @media (max-width: 700px) {
    align-items: flex-start;
    padding: 14px;
  }
`;

export const ModalContainer = styled.div`
  position: relative;
  width: ${({$width}) =>
          typeof $width === "number"
                  ? `${$width}px`
                  : $width};
  max-width: 100%;
  max-height: calc(100vh - 48px);
  overflow: hidden;
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  background-color: #fff;
  box-shadow:
          0 18px 50px rgba(0, 0, 0, 0.18),
          0 4px 12px rgba(0, 0, 0, 0.08);
  animation: ${modalEnter} 0.22s ease-out;

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 3px;
    background-color: #f5811f;
    content: "";
  }

  @media (max-width: 700px) {
    width: 100%;
    max-height: calc(100vh - 28px);
  }
`;

const ModalHeaderContainer = styled.header`
  position: relative;
  padding: 21px 25px 17px;
  border-bottom: 1px solid #eeeeee;
  background-color: #fff;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
`;

const HeaderText = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
  color: #222;
  font-size: var(--font-size-19);
  font-weight: 700;
  line-height: 1.4;

  & > span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const HeaderAccent = styled.span`
  width: 4px;
  height: 18px;
  flex: 0 0 4px;
  border-radius: 2px;
  background-color: #f5811f;
`;

const HeaderDescription = styled.p`
  margin: 7px 42px 0 14px;
  color: #888;
  font-size: var(--font-size-13);
  line-height: 1.5;
`;

const CloseButton = styled.button`
  position: relative;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  background-color: transparent;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &::before,
  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 14px;
    height: 1.5px;
    border-radius: 2px;
    background-color: #777;
    content: "";
  }

  &::before {
    transform:
      translate(-50%, -50%)
      rotate(45deg);
  }

  &::after {
    transform:
      translate(-50%, -50%)
      rotate(-45deg);
  }

  &:hover {
    border-color: #f1d4c2;
    background-color: #fff4e8;
  }

  &:hover::before,
  &:hover::after {
    background-color: #f5811f;
  }

  &:focus-visible {
    outline: 1px solid #f5811f;
    outline-offset: 2px;
  }

  & > span {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }
`;

const ModalBodyContainer = styled.main`
  max-height: calc(100vh - 190px);
  overflow-y: auto;
  padding: 22px 25px 25px;
  background-color: #fff;
  scrollbar-width: thin;
  scrollbar-color:
    #c9c9c9 transparent;

  &::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background-color: #c9c9c9;
  }

  @media (max-width: 700px) {
    max-height: calc(100vh - 170px);
    padding: 18px;
  }
`;

const ModalFooterContainer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 25px;
  border-top: 1px solid #eeeeee;
  background-color: #fafafa;

  button {
    min-width: 110px;
    height: 38px;
    margin: 0;
    padding: 0 18px;
    border-radius: 6px;
    font-size: var(--font-size-14);
    font-weight: 600;
  }

  @media (max-width: 700px) {
    padding: 14px 18px;

    button {
      min-width: 0;
      flex: 1;
    }
  }
`;